/**
 * Which supplier each part of a run of estimates is bought from.
 *
 * Jonathon, 2026-10-30: "when we're selecting the estimate template and we're
 * selecting shingle vs metal options we need to be able to pick two different
 * suppliers because metal is sold one place and shingles etc are sold at
 * another place."
 *
 * Until then the estimate builder had one Supplier box for the whole run. A
 * shingle option and a metal option ticked together were both ordered from it,
 * so one of the two material orders always went to the wrong place. Now the
 * builder asks for a supplier per material — one per path that has something
 * ticked on it — and every package and every area in another material is
 * bought from its own material's supplier.
 *
 * A path nobody has chosen for yet buys each template from the supplier that
 * template names itself, so a metal template that always goes to the metal yard
 * still does without anyone touching the box, and a metal porch whose package
 * names another yard still goes there. A template that names none takes the
 * default of the first template on its path that does, which is also what the
 * untouched box shows. Once a person chooses, that choice stands for the whole
 * path — even the blank one, which writes an order with no supplier for
 * somebody to fill in, as the single box always has.
 */

import {
  purchasePathOf,
  ROOF_PATHS,
  type PathTemplate,
  type RoofPath,
} from "./templateReadiness.js";

/** What choosing a supplier needs from a template. */
export interface SupplierTemplate extends Pick<PathTemplate, "roof_type"> {
  default_supplier_id?: string | null;
}

/** What the rep chose, per path. A path left out has not been touched. */
export type SuppliersChosen = Partial<Record<RoofPath, string>>;

/** One Supplier box on the screen: a path, and what it is set to. */
export interface PathSupplier {
  path: RoofPath;
  /** Empty when nobody chose one and no template names one. */
  supplierId: string;
}

/**
 * The Supplier boxes a run shows, in path order: one for every path something
 * being generated is bought on, packages and areas alike.
 */
export function suppliersByPath(
  inPlay: readonly SupplierTemplate[],
  chosen: SuppliersChosen,
): PathSupplier[] {
  return ROOF_PATHS.filter((path) =>
    inPlay.some((template) => purchasePathOf(template) === path),
  ).map((path) => ({ path, supplierId: supplierOnPath(path, inPlay, chosen) }));
}

/** What a path is bought from: the choice, else its templates' default. */
export function supplierOnPath(
  path: RoofPath,
  inPlay: readonly SupplierTemplate[],
  chosen: SuppliersChosen,
): string {
  const picked = chosen[path];
  if (picked !== undefined) return picked;
  return (
    inPlay.find(
      (template) =>
        purchasePathOf(template) === path && template.default_supplier_id,
    )?.default_supplier_id ?? ""
  );
}

/**
 * The supplier one template's parts are ordered from, or null for none.
 *
 * The choice for its path when there is one, even a blank one; otherwise the
 * template's own default before its path's, so one metal package's yard never
 * overrides the yard another metal package on the run names for itself.
 */
export function supplierForTemplate(
  template: SupplierTemplate,
  inPlay: readonly SupplierTemplate[],
  chosen: SuppliersChosen,
): string | null {
  const path = purchasePathOf(template);
  const picked = chosen[path];
  if (picked !== undefined) return picked || null;
  return (
    template.default_supplier_id || supplierOnPath(path, inPlay, chosen) || null
  );
}
